import Flex from "@/components/flex"
import { AlertTriangle, CheckCircle2 } from "lucide-react"
import { analysisStore } from "@/routes/apt/[apt_code]/analysis/analysis-store"
import { anomalyStore } from "./anomaly-store"
import { RESOURCE_KEYS, RESOURCE_META } from "@/constants/resource-const"
import { langStore } from "@/stores/lang-store"
import { formatMessage } from "@/utils/format-message"
import { useParams } from "react-router-dom"
import { overviewStore } from "../../overview-store"
import AnomalyCard from "./anomaly-card"
import { anomalyBundle } from "./anomaly-const"
import Wrapper from "@/components/wrapper"

const AnomalyView = () => {
  const { lang } = langStore()
  const { selectedTab } = overviewStore()
  const { region, aptCode } = useParams()
  const { filteredLeakUnits } = analysisStore()
  const { negativeUsageMap } = anomalyStore()

  const createCard = ({ type, title, desc, icon, count, countText, tone = "red", breakdown }) => {
    const isWarning = count > 0

    const palette = {
      red: { color: "text-red-500", bg: "bg-red-50" },
      sky: { color: "text-sky-500", bg: "bg-sky-50" }
    }

    return {
      type,
      title,
      desc,
      icon,
      path: `/apt/${region}/${aptCode}/analysis?resource=${type}`,
      value: isWarning ? countText : anomalyBundle.normal.status[lang],

      status: isWarning ? "warning" : "normal",

      color: isWarning ? palette[tone].color : "text-green-500",

      bg: isWarning ? palette[tone].bg : "bg-green-50",

      breakdown
    }
  }

  const negativeData = negativeUsageMap?.[selectedTab.id] || {}

  const leakCount = filteredLeakUnits?.length || 0

  const totalNegative = RESOURCE_KEYS.reduce((sum, key) => sum + (negativeData[key] || 0), 0)

  // const vacantCount = 4

  const negativeBreakdown = RESOURCE_KEYS.filter((key) => negativeData[key] > 0).map((key) => ({
    key,
    label: RESOURCE_META[key].label[lang],
    count: negativeData[key]
  }))

  const negativeDesc =
    negativeBreakdown.length === 0
      ? anomalyBundle.negative.none[lang]
      : negativeBreakdown
          .map(
            (item) =>
              `${item.label} : ${formatMessage(anomalyBundle.negative.count[lang], {
                count: item.count
              })}`
          )
          .join(", ")

  const contents = [
    createCard({
      type: "leak",

      title: anomalyBundle.leak.title[lang],
      desc: anomalyBundle.leak.desc[lang],
      icon: AlertTriangle,

      count: leakCount,

      countText: formatMessage(anomalyBundle.negative.count[lang], { count: leakCount })
    }),

    createCard({
      type: "anomaly",

      title: anomalyBundle.negative.title[lang],
      desc: negativeDesc,
      icon: AlertTriangle,

      count: totalNegative,

      countText: formatMessage(anomalyBundle.negative.count[lang], { count: totalNegative }),
      breakdown: negativeBreakdown
    })
    // ! 빈집 닫아놓기
    // createCard({
    //   type: "vacant",

    //   title: anomaly.vacant.title[lang],
    //   desc: anomaly.vacant.desc[lang],
    //   icon: AlertTriangle,

    //   count: vacantCount,

    //   countText: formatMessage(anomaly.vacant.count[lang], { count: vacantCount }),
    //   tone: "sky"
    // })
  ]

  const isNormal = contents.every((item) => item.status === "normal")

  return (
    <Wrapper.shadow className="p-5 flex flex-col gap-5 bg-white rounded-2xl md:min-w-90 h-full">
      <div>
        <h2 className="text-lg font-bold text-gray-800">{anomalyBundle.title[lang]}</h2>

        <p className="text-xs text-gray-400 mt-1">{anomalyBundle.description[lang]}</p>
      </div>

      <Flex className="flex-col flex-1" gap={4}>
        {isNormal ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <CheckCircle2 className="w-10 h-10 text-green-500 mb-2" />

            <p className="font-semibold text-gray-700">{anomalyBundle.normal.title[lang]}</p>

            <p className="text-sm text-gray-400 mt-1">{anomalyBundle.normal.desc[lang]}</p>
          </div>
        ) : (
          contents.map((item) => (
            <AnomalyCard
              key={item.type}
              item={item}
              lang={lang}
              countText={anomalyBundle.negative.count[lang]}
            />
          ))
        )}
      </Flex>
    </Wrapper.shadow>
  )
}

export default AnomalyView
